<template>
  <div>
    <div class="container">
      <h3 class="page-title">Change Password</h3>

      <v-form ref="form">
        <v-text-field
          name="password"
          label="Current Password"
          v-model="currentPassword"
          :type="showCurrent ? 'text' : 'password'"
          :rules="[rules.required]"
          variant="outlined"
          density="compact"
          autocomplete="current-password"
          required
          prepend-inner-icon="mdi-form-textbox-password"
          class="mb-2"
        >
          <template #append-inner>
            <v-btn icon variant="text" tabindex="-1" @click.stop="showCurrent = !showCurrent">
              <v-icon>{{ showCurrent ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
            </v-btn>
          </template>
        </v-text-field>

        <v-text-field
          name="new-password"
          label="New Password"
          v-model="newPassword"
          :type="showNew ? 'text' : 'password'"
          :rules="[rules.required, rules.passwordPolicy]"
          :hint="PASSWORD_HINT"
          variant="outlined"
          density="compact"
          autocomplete="new-password"
          required
          prepend-inner-icon="mdi-form-textbox-password"
          class="mb-2"
        >
          <template #append-inner>
            <v-btn icon variant="text" tabindex="-1" @click.stop="showNew = !showNew">
              <v-icon>{{ showNew ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
            </v-btn>
          </template>
        </v-text-field>

        <v-text-field
          name="confirm-new-password"
          label="Confirm New Password"
          v-model="confirmPassword"
          :type="showConfirm ? 'text' : 'password'"
          :rules="[rules.required, rules.matchesNew]"
          variant="outlined"
          density="compact"
          autocomplete="new-password"
          required
          prepend-inner-icon="mdi-form-textbox-password"
          class="mb-2"
        >
          <template #append-inner>
            <v-btn icon variant="text" tabindex="-1" @click.stop="showConfirm = !showConfirm">
              <v-icon>{{ showConfirm ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
            </v-btn>
          </template>
        </v-text-field>

        <div class="d-flex align-items-end justify-end px-3">
          <v-btn
            id="update-password"
            @click="updatePassword"
            variant="outlined"
            rounded="0"
            :loading="loading"
            :disabled="loading"
            style="height: 40px !important"
          >
            <v-icon start>mdi-content-save</v-icon>
            <span>Update Password</span>
          </v-btn>
        </div>
      </v-form>

      <!-- MFA Section -->
      <v-divider class="my-8"></v-divider>

      <h3 class="page-title">Two-Factor Authentication (MFA)</h3>

      <div v-if="mfaLoading" class="d-flex align-center mb-4">
        <v-progress-circular indeterminate size="20" width="2" class="mr-3"></v-progress-circular>
        <span class="text-body-2">Loading MFA status...</span>
      </div>

      <template v-else-if="mfaStatusError">
        <v-alert type="warning" variant="tonal" density="compact" class="mb-4" data-cy="mfa-status-error">
          {{ mfaStatusError }}
        </v-alert>

        <v-btn
          @click="loadMfaStatus"
          variant="outlined"
          rounded="0"
          :loading="mfaLoading"
          prepend-icon="mdi-refresh"
          data-cy="mfa-status-retry-btn"
        >
          Retry
        </v-btn>
      </template>

      <template v-else>
        <!-- MFA Enabled State -->
        <template v-if="mfaEnabled">
          <v-alert type="success" variant="tonal" density="compact" class="mb-4" data-cy="mfa-enabled-alert">
            Two-factor authentication is on. You'll enter a code from your authenticator app each time you sign in.
          </v-alert>

          <v-btn
            @click="showDisableMfaDialog = true"
            variant="outlined"
            color="error"
            rounded="0"
            prepend-icon="mdi-shield-off"
            data-cy="mfa-disable-btn"
          >
            Disable MFA
          </v-btn>
        </template>

        <!-- MFA Disabled State -->
        <template v-else>
          <p class="text-body-2 mb-4">
            Add an extra layer of security to your account. Once enabled, you'll enter a
            one-time code from your authenticator app (Google Authenticator, Authy, etc.) whenever you sign in.
          </p>

          <v-btn
            @click="startMfaSetup"
            variant="outlined"
            rounded="0"
            :loading="mfaSetupLoading"
            prepend-icon="mdi-shield-key"
            data-cy="mfa-enable-btn"
          >
            Enable MFA
          </v-btn>
        </template>

        <!-- MFA only ever applies to the email/password login path; federated
             (Google / SSO) logins are secured by the provider. -->
        <p class="text-caption text-medium-emphasis mt-4 mb-0">
          Applies when you sign in with your email and password. If you also sign in with
          Google or SSO, that login is secured by your provider.
        </p>
      </template>

      <!-- MFA Setup Dialog -->
      <v-dialog v-model="mfaSetupActive" max-width="500" persistent>
        <v-card>
          <v-card-title class="text-h5">Set up your authenticator app</v-card-title>
          <v-card-text>
            <div class="text-body-2 mb-3">
              <strong>Step 1:</strong> Scan this QR code with your authenticator app (Google Authenticator, Authy, Microsoft Authenticator, etc.)
            </div>

            <div class="d-flex justify-center mb-3">
              <img v-if="mfaQrDataUrl" :src="mfaQrDataUrl" alt="MFA QR Code" style="width: 200px; height: 200px;" />
              <v-progress-circular v-else indeterminate size="50"></v-progress-circular>
            </div>

            <div class="text-body-2 mb-1">Or enter this secret key manually:</div>
            <v-text-field
              :model-value="mfaSecretCode"
              readonly
              variant="outlined"
              density="compact"
              class="mb-3"
              prepend-inner-icon="mdi-key"
              append-inner-icon="mdi-content-copy"
              @click:append-inner="copySecret"
            ></v-text-field>

            <div class="text-body-2 mb-3">
              <strong>Step 2:</strong> Enter the 6-digit code from your authenticator app to verify setup.
            </div>

            <v-text-field
              v-model="mfaVerifyCode"
              label="Verification Code"
              variant="outlined"
              density="compact"
              maxlength="6"
              :rules="[v => !v || /^\d{6}$/.test(v) || 'Enter a 6-digit code.']"
              prepend-inner-icon="mdi-numeric"
              @keyup.enter="confirmMfaSetup"
              data-cy="mfa-verify-code-input"
            ></v-text-field>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey" variant="text" @click="cancelMfaSetup" :disabled="mfaConfirmLoading">
              Cancel
            </v-btn>
            <v-btn
              variant="flat"
              color="primary"
              @click="confirmMfaSetup"
              :loading="mfaConfirmLoading"
              :disabled="!/^\d{6}$/.test(mfaVerifyCode)"
              data-cy="mfa-verify-enable-btn"
            >
              Verify & Enable
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Disable MFA Confirmation Dialog -->
      <v-dialog v-model="showDisableMfaDialog" max-width="450">
        <v-card>
          <v-card-title class="text-h5">Disable MFA?</v-card-title>
          <v-card-text>
            This will remove two-factor authentication from your email/password login. You can re-enable it at any time.
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey" variant="text" @click="showDisableMfaDialog = false" :disabled="mfaDisableLoading">
              Cancel
            </v-btn>
            <v-btn color="error" variant="flat" @click="disableMfa" :loading="mfaDisableLoading" data-cy="mfa-disable-confirm-btn">
              Disable MFA
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Session Management Section -->
      <v-divider class="my-8"></v-divider>

      <h3 class="page-title">Session Management</h3>
      <p class="text-body-2 mb-4">
        If you suspect unauthorized access to your account, you can invalidate all active sessions.
        This will prevent access from being extended on any device, though active sessions may remain
        valid for up to 5 minutes.
      </p>

      <v-btn
        @click="logoutEverywhere"
        variant="outlined"
        color="error"
        rounded="0"
        :loading="logoutLoading"
        :disabled="logoutLoading"
        prepend-icon="mdi-logout-variant"
      >
        Logout from All Devices
      </v-btn>

      <!-- Confirmation Dialog -->
      <v-dialog v-model="showLogoutDialog" max-width="550">
        <v-card>
          <v-card-title class="text-h5">Logout from All Devices?</v-card-title>
          <v-card-text>
            <p class="mb-3">
              This will invalidate all refresh tokens, preventing sessions from being extended on any device.
              You'll be logged out immediately from this device, and other devices will lose access within
              approximately 5 minutes.
            </p>
            <p class="mb-3">
              <strong>Use this if:</strong>
            </p>
            <ul class="logout-bullets">
              <li>You suspect unauthorized access to your account</li>
              <li>You've lost a device</li>
              <li>You want to revoke all active sessions for security</li>
            </ul>
            <p class="text-caption logout-note mt-3">
              <strong>Note:</strong> Active sessions may remain
              functional for up to 5 minutes before being fully invalidated.
            </p>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="grey"
              variant="text"
              @click="showLogoutDialog = false"
              :disabled="logoutLoading"
            >
              Cancel
            </v-btn>
            <v-btn
              color="error"
              variant="flat"
              @click="confirmLogoutEverywhere"
              :loading="logoutLoading"
              :disabled="logoutLoading"
            >
              Yes, Logout Everywhere
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import cognitoAuth from '@/cognito'
import { authHelpers } from '@/api/authHelpers'
import { authProxyApi } from '@/api/authProxyApi'
import { passwordValidationRule, PASSWORD_HINT } from '@/helpers/passwordValidation'
import { useBrand } from '@/composables/useBrand'

const currentPassword = ref('')
const newPassword = ref('')
const confirmPassword = ref('')

const showCurrent = ref(false)
const showNew = ref(false)
const showConfirm = ref(false)

const loading = ref(false)
const logoutLoading = ref(false)
const showLogoutDialog = ref(false)
const form = ref(null)

// MFA state
const mfaLoading = ref(false)
const mfaEnabled = ref(false)
const mfaStatusError = ref(null)
const mfaSetupActive = ref(false)
const mfaSetupLoading = ref(false)
const mfaConfirmLoading = ref(false)
const mfaDisableLoading = ref(false)
const mfaSecretCode = ref('')
const mfaQrDataUrl = ref('')
const mfaVerifyCode = ref('')
const showDisableMfaDialog = ref(false)

const store = useStore()
const router = useRouter()
const brand = useBrand()

const userEmail = computed(() => store.state.userInfo)

onMounted(async () => {
  if (userEmail.value) {
    await loadMfaStatus()
  }
})

async function loadMfaStatus () {
  mfaLoading.value = true
  mfaStatusError.value = null
  try {
    const session = await cognitoAuth.getSession()
    const token = session.getAccessToken().getJwtToken()

    const res = await authProxyApi.mfaStatus({ accessToken: token })
    if (res?.ok) {
      mfaEnabled.value = res.mfaEnabled
    } else {
      mfaStatusError.value = res?.message || 'Unable to load MFA status. Please try again.'
    }
  } catch (e) {
    console.error('Failed to load MFA status:', e)
    mfaStatusError.value = 'Unable to load MFA status. Please try again.'
  } finally {
    mfaLoading.value = false
  }
}

async function startMfaSetup () {
  mfaSetupLoading.value = true
  try {
    const session = await cognitoAuth.getSession()
    const token = session.getAccessToken().getJwtToken()

    const res = await authProxyApi.mfaSetup({ accessToken: token })
    if (!res?.ok || !res?.secretCode) {
      throw new Error('Failed to start MFA setup.')
    }

    mfaSecretCode.value = res.secretCode
    mfaSetupActive.value = true

    // Generate QR code data URL using a otpauth URI
    const issuer = brand.name
    const otpauthUrl = `otpauth://totp/${issuer}:${userEmail.value}?secret=${res.secretCode}&issuer=${issuer}`
    mfaQrDataUrl.value = await generateQrDataUrl(otpauthUrl)
  } catch (e) {
    console.error('MFA setup error:', e)
    store.commit('snackbar/showMessage', {
      content: 'Failed to start MFA setup. Please try again.',
      color: 'red'
    }, { root: true })
  } finally {
    mfaSetupLoading.value = false
  }
}

async function generateQrDataUrl (text) {
  // Simple QR code generation using a canvas-based approach
  // We'll use a lightweight inline QR generator
  const { default: QRCode } = await import('qrcode')
  return QRCode.toDataURL(text, { width: 200, margin: 2 })
}

async function confirmMfaSetup () {
  if (!/^\d{6}$/.test(mfaVerifyCode.value)) return

  mfaConfirmLoading.value = true
  try {
    const session = await cognitoAuth.getSession()
    const token = session.getAccessToken().getJwtToken()

    const res = await authProxyApi.mfaConfirm({
      accessToken: token,
      code: mfaVerifyCode.value,
      email: userEmail.value
    })

    if (!res?.ok) {
      throw new Error(res?.message || 'Invalid code. Please try again.')
    }

    mfaEnabled.value = true
    mfaSetupActive.value = false
    mfaSecretCode.value = ''
    mfaQrDataUrl.value = ''
    mfaVerifyCode.value = ''

    store.commit('snackbar/showMessage', {
      content: 'MFA has been enabled successfully.',
      color: 'green'
    }, { root: true })
  } catch (e) {
    console.error('MFA confirm error:', e)
    store.commit('snackbar/showMessage', {
      content: e.message || 'Failed to verify code. Please try again.',
      color: 'red'
    }, { root: true })
  } finally {
    mfaConfirmLoading.value = false
  }
}

function cancelMfaSetup () {
  mfaSetupActive.value = false
  mfaSecretCode.value = ''
  mfaQrDataUrl.value = ''
  mfaVerifyCode.value = ''
}

async function disableMfa () {
  mfaDisableLoading.value = true
  try {
    const session = await cognitoAuth.getSession()
    const token = session.getAccessToken().getJwtToken()

    const res = await authProxyApi.mfaDisable({
      accessToken: token
    })

    if (!res?.ok) {
      throw new Error(res?.message || 'Failed to disable MFA.')
    }

    mfaEnabled.value = false
    showDisableMfaDialog.value = false

    store.commit('snackbar/showMessage', {
      content: 'MFA has been disabled.',
      color: 'blue'
    }, { root: true })
  } catch (e) {
    console.error('MFA disable error:', e)
    store.commit('snackbar/showMessage', {
      content: e.message || 'Failed to disable MFA. Please try again.',
      color: 'red'
    }, { root: true })
  } finally {
    mfaDisableLoading.value = false
  }
}

function copySecret () {
  navigator.clipboard.writeText(mfaSecretCode.value)
  store.commit('snackbar/showMessage', {
    content: 'Secret key copied to clipboard.',
    color: 'blue',
    timeout: 2000
  }, { root: true })
}

const rules = {
  required: v => (!!v || v === 0) || 'This field is required.',
  passwordPolicy: passwordValidationRule,
  matchesNew: v =>
    v === newPassword.value || 'Confirmation must match the new password.'
}

async function updatePassword () {
  const { valid } = await form.value.validate()
  if (!valid) return

  loading.value = true
  try {
    cognitoAuth.changePassword(currentPassword.value, newPassword.value, async (err, result) => {
      if (err) {
        // Map Cognito errors to clearer user-facing messages
        const errType = err?.code || err?.__type || err?.name
        const errMessage = typeof err?.message === 'string' ? err.message : ''

        let content = 'Incorrect password. Please try again.'
        let color = 'red'
        let timeout = 4000

        if (errType === 'LimitExceededException' || errMessage.includes('Attempt limit exceeded')) {
          content = 'Too many attempts. Please try again in a few minutes.'
          color = 'red'
          timeout = 6000
        } else if (errType === 'InvalidPasswordException') {
          content = 'New password does not meet requirements. Use at least 8 characters with lowercase, uppercase and a number.'
          color = 'red'
        } else if (errType === 'NotAuthorizedException') {
          content = 'Incorrect password. Please try again.'
        }

        store.commit('snackbar/showMessage', {
          content,
          color,
          location: 'center',
          timeout
        }, { root: true })
        loading.value = false
      } else {
        // Immediately logout and redirect to login where the success message is shown
        store.commit('logout')
        loading.value = false
        router.push('/login?reason=password_changed')
      }
    })
  } catch (error) {
    console.error('Error updating password:', error)
    store.commit('snackbar/showMessage', {
      content: 'An error occurred while updating password',
      color: 'red'
    }, { root: true })
    loading.value = false
  }
}

function logoutEverywhere () {
  showLogoutDialog.value = true
}

async function confirmLogoutEverywhere () {
  logoutLoading.value = true
  try {
    await authHelpers.globalLogoutAndClearStore()

    store.commit('snackbar/showMessage', {
      content: 'All refresh tokens have been revoked. Other sessions will expire within 5 minutes.',
      color: 'blue'
    }, { root: true })

    // Wait a moment for user to see the message
    await new Promise(resolve => setTimeout(resolve, 500))

    router.push('/login?reason=global_logout')
  } catch (error) {
    console.error('Error logging out everywhere:', error)
    store.commit('snackbar/showMessage', {
      content: 'An error occurred while logging out. Please try again.',
      color: 'red'
    }, { root: true })
  } finally {
    logoutLoading.value = false
    showLogoutDialog.value = false
  }
}
</script>

<style scoped>
.page-title {
  margin-bottom: 32px;
  font-size: 20px;
  font-weight: 700;
  color: var(--dark-4, #363A40);
}

.logout-bullets {
  padding-left: 24px;
  margin-left: 8px;
  list-style-position: outside;
}

.logout-note {
  color: var(--text-light);
}
</style>
